import React from 'react';
import { Info, Clock, Bell, Calendar, Target, Zap, AlertTriangle } from 'lucide-react';

/**
 * Информационный компонент с описанием таймера и советами
 */
const InfoContent = () => {
  return (
    <article className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mt-8">
      <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
        <Info className="w-6 h-6 text-blue-500" />
        Всё о таймерах: типы, применение, советы
      </h2>
      
      <div className="prose prose-indigo dark:prose-invert max-w-none prose-sm sm:prose-base">
        <h3 className="flex items-center gap-2">
          <Clock className="w-5 h-5 text-indigo-500" />
          Что такое таймер обратного отсчёта?
        </h3>
        <p>
          <strong>Таймер обратного отсчёта</strong> — это инструмент для отслеживания времени, оставшегося до определённого события или дедлайна. Он помогает визуализировать ход времени и создает ощущение срочности, что особенно полезно для планирования и мотивации.
        </p>

        <h3 className="flex items-center gap-2">
          <Target className="w-5 h-5 text-indigo-500" />
          Основные типы таймеров
        </h3>
        <ul>
          <li><strong>Обратный отсчёт до даты:</strong> Показывает время, оставшееся до конкретной календарной даты (например, до Нового года или Дня Победы).</li>
          <li><strong>Прямой отсчёт от даты:</strong> Отсчитывает время, прошедшее с момента какого-либо события (например, сколько дней прошло с начала проекта).</li>
          <li><strong>Таймер с заданным интервалом:</strong> Отсчитывает заданное количество часов, минут и секунд. Идеален для рабочих сессий, упражнений или приготовления пищи.</li>
          <li><strong>Повторяющийся таймер:</strong> Циклически повторяет отсчёт по достижении нуля. Полезен для техники Помодоро или интервальных тренировок.</li>
        </ul>

        <h3 className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-indigo-500" />
          Популярные случаи использования
        </h3>
        <p>
          Таймеры обратного отсчёта широко используются в различных сферах жизни:
        </p>
        <ul>
          <li><strong>Праздники и знаменательные даты:</strong> Обратный отсчёт до Нового года, Дня Победы, дня рождения или годовщины.</li>
          <li><strong>Рабочие дедлайны:</strong> Контроль сроков выполнения проектов и задач.</li>
          <li><strong>Образование:</strong> Отсчёт до экзаменов, каникул или начала учебного года.</li>
          <li><strong>Продуктивность:</strong> Техника Помодоро, таймеры для концентрации внимания.</li>
          <li><strong>Покупки и продажи:</strong> Срок действия акций, время до окончания распродажи.</li>
          <li><strong>Спорт и фитнес:</strong> Интервальные тренировки, контроль времени упражнений.</li>
          <li><strong>Приготовление пищи:</strong> Контроль времени приготовления различных блюд.</li>
        </ul>

        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800 my-6">
          <h4 className="text-blue-700 dark:text-blue-300 font-medium">Патриотические даты России</h4>
          <p className="text-blue-600 dark:text-blue-400 text-sm mb-2">
            Наш таймер имеет специальные темы и предустановки для важных государственных праздников России:
          </p>
          <ul className="text-blue-600 dark:text-blue-400 text-sm mb-0">
            <li>9 мая - День Победы (тема в цветах Георгиевской ленты)</li>
            <li>12 июня - День России (тема в цветах российского флага)</li>
            <li>4 ноября - День народного единства</li>
            <li>23 февраля - День защитника Отечества</li>
            <li>1 января - Новый год и другие праздники</li>
          </ul>
        </div>

        <h3 className="flex items-center gap-2">
          <Bell className="w-5 h-5 text-indigo-500" />
          Функции и возможности нашего таймера
        </h3>
        <ul>
          <li><strong>Гибкая настройка:</strong> Выбор между таймером до указанной даты или простым отсчётом часов, минут и секунд.</li>
          <li><strong>Обратный и прямой отсчёт:</strong> Возможность отсчитывать время как до события, так и после него.</li>
          <li><strong>Настраиваемый дизайн:</strong> Выбор темы оформления, включая специальные российские темы.</li>
          <li><strong>Уведомления:</strong> Звуковые и браузерные уведомления по окончании отсчёта.</li>
          <li><strong>Сохранение таймеров:</strong> Возможность создавать и сохранять несколько таймеров для разных событий.</li>
          <li><strong>Предустановки:</strong> Готовые настройки для российских праздников и популярных интервалов времени.</li>
        </ul>

        <h3 className="flex items-center gap-2">
          <Zap className="w-5 h-5 text-orange-500" />
          Советы по эффективному использованию таймеров
        </h3>
        <ol>
          <li><strong>Техника Помодоро:</strong> Разбивайте работу на короткие интервалы (обычно 25 минут) с перерывами. Таймер помогает строго следовать этому методу.</li>
          <li><strong>Правило "2 минут":</strong> Если задача занимает менее 2 минут, сделайте её сразу, а не откладывайте.</li>
          <li><strong>Начинайте с малого:</strong> Установите таймер на 5-10 минут, чтобы начать сложную задачу. Часто это помогает преодолеть прокрастинацию.</li>
          <li><strong>Используйте таймер для ограничения времени:</strong> Выделяйте фиксированное время на задачи, которые могут затянуться (например, проверка почты).</li>
          <li><strong>Создавайте ритуалы:</strong> Использование таймера может стать частью продуктивного ритуала.</li>
        </ol>

        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-100 dark:border-yellow-800 my-6">
          <h4 className="text-yellow-700 dark:text-yellow-300 font-medium flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Важно помнить про уведомления
          </h4>
          <p className="text-yellow-600 dark:text-yellow-400 text-sm mb-0">
            Чтобы не пропустить момент окончания таймера, убедитесь, что:
          </p>
          <ul className="text-yellow-600 dark:text-yellow-400 text-sm mb-0">
            <li>Вы разрешили браузерные уведомления (при первом использовании)</li>
            <li>Выбран подходящий звук уведомления</li>
            <li>Звук на устройстве включен, если выбрано звуковое уведомление</li>
          </ul>
        </div>

        <h3>Психология времени и таймеров</h3>
        <p>
          Использование таймеров имеет несколько психологических эффектов:
        </p>
        <ul>
          <li><strong>Эффект Зейгарник:</strong> Незавершенные задачи запоминаются лучше завершенных. Таймер помогает создать ощущение завершенности.</li>
          <li><strong>Визуализация времени:</strong> Наглядное представление оставшегося времени делает его более осязаемым.</li>
          <li><strong>Снижение стресса:</strong> Разбивая большие периоды времени на меньшие, мы делаем их менее пугающими.</li>
          <li><strong>Повышение мотивации:</strong> Видимый обратный отсчёт создает ощущение срочности и помогает бороться с прокрастинацией.</li>
        </ul>

        <p className="font-semibold mt-6">
          Наш таймер обратного отсчёта — это удобный инструмент для планирования времени, повышения продуктивности и отслеживания важных дат. Создайте свой таймер прямо сейчас!
        </p>
      </div>
    </article>
  );
};

export default InfoContent;